/**
 * Gate one: is there a working microphone, and is it actually picking anything up?
 *
 * `AudioContext` + `AnalyserNode` FFT, with the pass decision delegated to
 * `micCheckWindow.ts` — 800 ms of above-threshold audio accumulated inside a
 * rolling 3 s window, not 800 ms unbroken. That distinction is not a detail: the
 * unbroken rule ejected a real participant from the Aug-3 Craft Together study
 * because natural speech has gaps between words.
 *
 * The stream this acquires is handed onward to `usePeerVoice` rather than
 * stopped, so the participant grants microphone permission exactly once.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { MicFrame } from '../voice/micCheckWindow'
import { hasHeardEnough, pushFrame, shouldOfferManualOverride } from '../voice/micCheckWindow'

/**
 * What the microphone is asked for. Echo cancellation matters more here than
 * fidelity: both players are on laptop speakers and will otherwise hear
 * themselves back. Shared with the reload path, which asks again.
 */
export const MIC_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
}

export type MicCheckMethod = 'auto' | 'manual'

interface MicCheckProps {
  /** Where this gate sits among the ones this room shows. */
  step: number
  of: number
  /** The stream is passed on, not stopped — voice reuses it. */
  onPass(stream: MediaStream, method: MicCheckMethod): void
  onGiveUp(reason: string): void
}

type Status = 'requesting' | 'listening' | 'failed'

function describe(err: unknown): string {
  const name = (err as DOMException)?.name ?? 'UnknownError'
  if (name === 'NotAllowedError') return 'Your browser blocked the microphone. Allow it in the address bar, then reload.'
  if (name === 'NotFoundError') return 'No microphone found on this device.'
  if (name === 'NotReadableError') return 'Another app is using your microphone. Close it, then reload.'
  return `Your browser couldn't open the microphone (${name}).`
}

export function MicCheck({ step, of, onPass, onGiveUp }: MicCheckProps) {
  const [status, setStatus] = useState<Status>('requesting')
  const [level, setLevel] = useState(0)
  const [heard, setHeard] = useState(false)
  const [canOverride, setCanOverride] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const frameRef = useRef<number | null>(null)
  const framesRef = useRef<MicFrame[]>([])
  const listeningSinceRef = useRef<number | null>(null)
  const passedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    let context: AudioContext | null = null

    const stop = (): void => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      void context?.close().catch(() => undefined)
    }

    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream

        context = new AudioContext()
        const source = context.createMediaStreamSource(stream)
        const analyser = context.createAnalyser()
        analyser.fftSize = 256
        source.connect(analyser)
        const data = new Uint8Array(analyser.frequencyBinCount)
        listeningSinceRef.current = performance.now()
        setStatus('listening')

        const step = (): void => {
          analyser.getByteFrequencyData(data)
          const mean = data.reduce((sum, value) => sum + value, 0) / data.length
          const next = Math.min(100, Math.round(mean * 2))
          setLevel(next)

          const now = performance.now()
          framesRef.current = pushFrame(framesRef.current, now, next)
          if (hasHeardEnough(framesRef.current, now)) setHeard(true)
          if (listeningSinceRef.current !== null && shouldOfferManualOverride(listeningSinceRef.current, now)) {
            setCanOverride(true)
          }

          frameRef.current = requestAnimationFrame(step)
        }
        step()
      } catch (err) {
        if (cancelled) return
        setError(describe(err))
        setStatus('failed')
      }
    })()

    return () => {
      cancelled = true
      stop()
      // Only release the microphone if it is not being handed onward.
      if (!passedRef.current) {
        streamRef.current?.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }
    }
  }, [])

  const pass = useCallback(
    (method: MicCheckMethod) => {
      const stream = streamRef.current
      if (!stream) return
      passedRef.current = true
      onPass(stream, method)
    },
    [onPass],
  )

  // Advance on its own once we have heard a clear signal, with a beat of green
  // first so the participant sees why they moved on.
  useEffect(() => {
    if (status !== 'listening' || !heard) return
    const timer = setTimeout(() => pass('auto'), 900)
    return () => clearTimeout(timer)
  }, [status, heard, pass])

  return (
    <section className="gate gate-mic">
      <p className="gate-step">
        Step {step} of {of}
      </p>
      <h1>Can we hear you?</h1>

      <div className="meter" aria-label="microphone input level">
        <div
          className={`meter-fill${heard ? ' meter-fill-ok' : ''}`}
          style={{ width: `${status === 'listening' ? Math.max(level, 4) : 0}%` }}
        />
      </div>

      {status === 'requesting' && <p className="hint">Allow the microphone when your browser asks.</p>}
      {status === 'listening' && !heard && <p className="hint">Say a few words out loud.</p>}
      {heard && <p className="ok">Got it. We can hear you.</p>}
      {error && <p className="error">{error}</p>}

      <div className="gate-actions">
        {status === 'listening' && canOverride && !heard && (
          <button type="button" onClick={() => pass('manual')} data-testid="mic-continue">
            My mic works
          </button>
        )}
        {status === 'failed' && (
          <button type="button" onClick={() => onGiveUp(error ?? "Your microphone isn't available.")}>
            I can't use a microphone
          </button>
        )}
      </div>
    </section>
  )
}
